import React, { useMemo, useCallback, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "./AgGrid.css";

import PropTypes from "prop-types";
import moment from "moment";

import {
  Box,
  Stack,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Search,
  FileDownload,
  ViewColumn,
  Save,
  RestoreFromTrash,
  FilterAltOff,
  Refresh,
  Add,
} from "@mui/icons-material";

import {
  Whisper,
  Popover,
  Dropdown,
  Checkbox,
  CheckboxGroup,
  Message,
  useToaster,
} from "rsuite";
import ArrowDownIcon from "@rsuite/icons/ArrowDown";

import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "app/hooks/useAxiosPrivate";

// ─── Navigation routing map ──────────────────────────────────────────────────
// Centralises the cell-click routing logic that was previously a long if/else chain.
const getCellClickDestination = ({ GoTo, Source, event, handleViewModalOpen, navigate }) => {
  const { colDef, node } = event;
  const field = colDef.field;
  const data = node.data;
  const selected = node.isSelected();

  // Helper shortcuts
  const goTo = (path) => navigate(path);
  const viewModal = () => handleViewModalOpen?.();

  if (GoTo && (field === "ID" || field === "Reference")) {
    if (!selected) return;
    if (Source === "Followup" || Source === "FollowupMenu") {
      const isCLIA =
        data?.Type === "CLIAAgencyData" ||
        String(data?.Reference || data?.ID || "")
          .toUpperCase()
          .startsWith("AGENT");
      return isCLIA
        ? goTo(`/CLIAProspect/EditCLIAProspect/${data?.ProspectID}`)
        : goTo(`/Prospects/ViewProspect/${data?.ProspectID}`);
    }
    return goTo(`${GoTo}/${data?.UniqueID}`);
  }

  if (field === "ProspectID") return viewModal();
  if (field === "Name" && Source === "Family") return viewModal();

  if (Source === "PremiumPoint") {
    return field === "Reference" && data?.ReferenceID !== null
      ? goTo(`/Prospects/ViewProspect/${data?.ReferenceID}`)
      : viewModal();
  }

  if (Source === "PastePremiumPointNew") {
    return viewModal();
  }

  const modalOnlySources = [
    "CurrentMonthDue", "OutstandingDueList", "LICMaturityList",
    "SBPolicyList", "PastePremiumPoint", "LapsedPolicyDue",
  ];
  if (modalOnlySources.includes(Source)) return viewModal();

  if (GoTo && field === "FirstName" && Source === "User" && selected)
    return goTo(`${GoTo}/${data?.UniqueID}`);

  if (GoTo && (field === "fullname" || field === "ID") && (Source === "DahiyaMitra" || Source === "CLIA") && selected)
    return goTo(`${GoTo}/${data?.UniqueID}`);

  if (GoTo && Source === "Policy" && selected) {
    if (field === "RefID") return goTo(`/Prospects/ViewProspect/${data?.ReferenceID}`);
    if (field === "PolicyNo") return goTo(`${GoTo}/${data?.UniqueID}`);
  }

  const cliaRefSources = ["CLIAReference", "CLIAProspect", "CLIAAgencyData"];
  if (GoTo && cliaRefSources.includes(Source) && selected) {
    if (field === "RefID") return goTo(`${GoTo}/${data?.UniqueID}`);
  }

  const servicesSources = ["Bond", "ENach", "MinorDAB", "MinorNomination"];
  if (GoTo && servicesSources.includes(Source) && selected) {
    if (field === "RefID") return goTo(`/Prospects/ViewProspect/${data?.ReferenceID}`);
    if (field === "PolicyNo") return goTo(`${GoTo}/${data?.UniqueID}`);
  }

  if (GoTo && Source === "PolicyService" && selected) {
    if (field === "Reference") return goTo(`/Prospects/ViewProspect/${data?.ReferenceID}`);
    if (field === "PolicyNo" && data?.PolicyNoID !== "00000000-0000-0000-0000-000000000000")
      return goTo(`/Policy/View/${data?.PolicyNoID}`);
    if (field === "ServiceID") return goTo(`${GoTo}/${data?.UniqueID}`);
  }

  if (Source === "Followup" || Source === "FollowupMenu" || Source === "LICInvoice") {
    if (field === "Reference" || field === "ID") {
      const isCLIA =
        data?.Type === "CLIAAgencyData" ||
        String(data?.Reference || data?.ID || "")
          .toUpperCase()
          .startsWith("AGENT");
      return isCLIA
        ? goTo(`/CLIAProspect/EditCLIAProspect/${data?.ProspectID}`)
        : goTo(`/Prospects/ViewProspect/${data?.ProspectID}`);
    }
    if (["Result", "DiscussionCount", "InvoiceID"].includes(field)) return viewModal();
  }
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function AgGrid({
  columns = [
    { headerName: "Athlete", field: "athlete", minWidth: 180 },
    { field: "age" },
    { field: "country", minWidth: 150 },
    { field: "year" },
    { field: "date", minWidth: 150 },
    { field: "sport", minWidth: 150 },
    { field: "gold" },
    { field: "silver" },
    { field: "bronze" },
    { field: "total", hide: true },
  ],
  rowData = [
    {
      athlete: "Michael Phelps", age: 23, country: "United States",
      year: 2008, date: "24/08/2008", sport: "Swimming",
      gold: 8, silver: 0, bronze: 0, total: 8,
    },
  ],
  onGridReady,
  ExportFileName = `${moment().format("DDMMYYYYHHmmss")}.csv`,
  GoTo = "",
  showHeaderItems = true,
  passdata = false,
  QuickSearch = true,
  exportAllowed = true,
  ChooseColumns = true,
  showLoading = false,
  addMember = false,
  addDiscussion = false,
  handleOpen,
  onRowSelectedData,
  handleViewModalOpen,
  onDataChange,
  headerComponent,
  setFilteredRows,
  Source = "Followup",
  theme = "ag-theme-alpine",
  setRowStyle = false,
}) {
  const muiTheme = useTheme();
  const gridRef = useRef(null);
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const toaster = useToaster();

  const [selectedColumns, setSelectedColumns] = useState(() => {
    // Initialise with all visible columns
    return columns.filter((c) => !c.hide).map((c) => c.field);
  });
  const [columnDefs, setColumnDefs] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [settings, setSettings] = useState([]);
  const [filteredRowNodes, setFilteredRowNodes] = useState([]);
  const [currentRowIndex, setCurrentRowIndex] = useState(0);

  // ── Grid config ─────────────────────────────────────────────────────────────

  const defaultColDef = useMemo(() => ({
    flex: 1,
    editable: false,
    filter: true,
    minWidth: 150,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    enableCellChangeFlash: true,
    headerComponentParams: { menuIcon: "fa-bars" },
  }), []);

  const gridOptions = useMemo(() => ({
    getRowId: (params) => params.data.UniqueID,
    enableCellTextSelection: true,
    enableBrowserTooltips: true,
  }), []);

  const autoSizeStrategy = useMemo(() => ({ type: "fitGridWidth" }), []);

  const getRowStyle = useCallback((params) => {
    if (!setRowStyle) return null;
    if (params.data?.Type === "Credit") return { color: "green" };
    if (params.data?.Type === "Debit") return { color: "red" };
    return { color: "white" };
  }, [setRowStyle]);

  // ── Filtered row tracking ────────────────────────────────────────────────────

  const updateFilteredRowNodes = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    const nodes = [];
    api.forEachNodeAfterFilterAndSort((node) => {
      if (!node.group) nodes.push(node);
    });
    setFilteredRowNodes(nodes);
    setFilteredRows?.(nodes);
    setCurrentRowIndex(nodes.length > 0 ? 0 : -1);
  }, [setFilteredRows]);

  React.useEffect(() => {
    if (gridRef.current && rowData.length > 0) updateFilteredRowNodes();
  }, [rowData, updateFilteredRowNodes]);

  React.useEffect(() => {
    if (!gridRef.current || filteredRowNodes.length === 0) return;
    const node = filteredRowNodes[currentRowIndex];
    if (node) {
      gridRef.current.api.ensureIndexVisible(node.rowIndex);
      node.setSelected(true);
    }
  }, [currentRowIndex, filteredRowNodes]);

  // ── Loading overlay ──────────────────────────────────────────────────────────

  React.useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    if (showLoading) api.showLoadingOverlay();
    else api.hideOverlay();
  }, [showLoading]);

  // ── Column visibility from server settings ───────────────────────────────────

  React.useEffect(() => {
    axiosPrivate
      .post("GetSettings.php", { Source })
      .then((response) => {
        const result = response.data;
        if (result.status === 1) {
          setSelectedColumns(result.Columns);
          setColumnDefs(columns.filter((c) => result.Columns.includes(c.field)));
        }
      })
      .catch((e) => console.error("Failed to load grid settings:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep column defs in sync when column selection changes
  React.useEffect(() => {
    setColumnDefs(columns.filter((c) => selectedColumns.includes(c.field)));
  }, [selectedColumns, columns]);

  // ── Cell click routing ───────────────────────────────────────────────────────

  const onRowSelected = useCallback((event) => {
    getCellClickDestination({ GoTo, Source, event, handleViewModalOpen, navigate });
    onRowSelectedData?.(event.node.data);
  }, [GoTo, Source, navigate, handleViewModalOpen, onRowSelectedData]);

  // ── Quick filter ─────────────────────────────────────────────────────────────

  const onQuickFilterChanged = useCallback((e) => {
    setSearchText(e.target.value);
    gridRef.current?.api.setGridOption("quickFilterText", e.target.value);
  }, []);

  // ── Reset filters ────────────────────────────────────────────────────────────

  const resetFilters = useCallback(() => {
    gridRef.current?.api.setFilterModel(null);
    setSearchText("");
    gridRef.current?.api.setGridOption("quickFilterText", "");
  }, []);

  // ── Cell value change (inline edit) ─────────────────────────────────────────

  const onCellValueChanged = useCallback((event) => {
    const { colDef, data } = event;
    if ((colDef.field === "Status" || colDef.field === "LeadStatus") && Source === "Prospect") {
      onDataChange?.({
        UID: data.UniqueID, Status: data.Status,
        LeadStatus: data.LeadStatus, Type: Source, Data: "Status",
      });
    }
    if (colDef.field === "LeadStatus" && (Source === "Followup" || Source === "FollowupMenu")) {
      onDataChange?.({
        UID: data.ProspectID, LeadStatus: data.LeadStatus,
        Type: Source, Data: "LeadStatus",
      });
    }
  }, [Source, onDataChange]);

  // ── Settings persistence ─────────────────────────────────────────────────────

  const pushToast = useCallback((type, message) => {
    toaster.push(
      <Message type={type} bordered showIcon>
        <strong>{type === "success" ? "Success!" : "Error!"}</strong> {message}
      </Message>
    );
  }, [toaster]);

  const SaveSettings = useCallback(async () => {
    if (!gridRef.current) return;
    const columnState = gridRef.current.columnApi?.getColumnState();
    const settingsPayload = JSON.stringify({ columnState });
    try {
      const response = await axiosPrivate.post("SaveSettings.php", {
        Columns: selectedColumns,
        Source,
        Settings: settingsPayload,
      });
      const result = response.data;
      pushToast(result.status === 1 ? "success" : "error",
        result.status === 1 ? result.Success : "Error saving settings.");
    } catch (e) {
      console.error("Error saving settings:", e);
      pushToast("error", "Error saving settings.");
    }
  }, [axiosPrivate, selectedColumns, Source, pushToast]);

  const deleteSettings = useCallback(async () => {
    try {
      const response = await axiosPrivate.post("DeleteSettings.php", { Source, Settings: settings });
      const result = response.data;
      pushToast(result.status === 1 ? "success" : "error",
        result.status === 1 ? result.Success : "Error resetting settings.");
    } catch (e) {
      console.error("Error resetting settings:", e);
      pushToast("error", "Error resetting settings.");
    }
  }, [axiosPrivate, Source, settings, pushToast]);

  // ── Grid ready / settings application ────────────────────────────────────────

  const applySettings = useCallback((columnState, visibleColumns) => {
    const api = gridRef.current?.api;
    const colApi = gridRef.current?.columnApi;
    if (!api || !colApi || !columnState?.length) return;

    const newColumnDefs = columnState
      .map((field) => {
        const colDef = columns.find((c) => c.field === field.colId);
        return colDef ? { ...colDef, width: field.width } : null;
      })
      .filter(Boolean)
      .filter((c) => visibleColumns.includes(c.field));

    api.applyColumnState({ state: newColumnDefs, applyOrder: true });
    setColumnDefs(newColumnDefs);
  }, [columns]);

  const getdata = useCallback((params) => {
    axiosPrivate
      .post("GetSettings.php", { Source })
      .then((response) => {
        const result = response.data;
        if (result.status === 1) {
          setSettings(result.Settings);
          setSelectedColumns(result.Columns);
          applySettings(result.Settings?.columnState, result.Columns);
        }
        onGridReady?.(params);
      })
      .catch((e) => {
        console.error("Error loading grid:", e);
        onGridReady?.(params);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axiosPrivate, Source, applySettings, onGridReady]);

  const onBtnExport = useCallback(() => {
    gridRef.current?.api.exportDataAsCsv({ fileName: `${Source}-${ExportFileName}` });
  }, [Source, ExportFileName]);

  // ── Row count badge ──────────────────────────────────────────────────────────
  const rowCount = rowData?.length ?? 0;

  // ── Styles ───────────────────────────────────────────────────────────────────
  const toolbarSx = {
    px: 1.5,
    py: 1,
    mb: 0.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 1,
    background: alpha(muiTheme.palette.background.paper, 0.8),
    borderRadius: "8px 8px 0 0",
    borderBottom: `1px solid ${alpha(muiTheme.palette.divider, 0.6)}`,
  };

  const toolbarBtnSx = {
    fontSize: "0.75rem",
    py: 0.5,
    px: 1.25,
    minWidth: "unset",
    fontWeight: 500,
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: "100%", height: "600px", display: "flex", flexDirection: "column" }}>
      {showHeaderItems && (
        <Box sx={toolbarSx}>
          {/* Left toolbar group */}
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">

            {QuickSearch && (
              <TextField
                size="small"
                placeholder="Quick search…"
                value={searchText}
                onChange={onQuickFilterChanged}
                sx={{ width: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: "1rem", color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2, fontSize: "0.8125rem", height: 32 },
                }}
              />
            )}

            {exportAllowed && (
              <Tooltip title="Export CSV">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownload sx={{ fontSize: "0.9rem !important" }} />}
                  onClick={onBtnExport}
                  sx={toolbarBtnSx}
                >
                  Export
                </Button>
              </Tooltip>
            )}

            {ChooseColumns && (
              <Whisper
                placement="bottomStart"
                trigger="click"
                speaker={({ onClose, left, top, className }, ref) => (
                  <Popover
                    ref={ref}
                    className={className}
                    style={{ left, top, maxHeight: 400, overflowY: "auto", minWidth: 180 }}
                    full
                  >
                    <Dropdown.Menu>
                      <CheckboxGroup
                        name="columns-group"
                        style={{ padding: "8px 12px" }}
                        value={selectedColumns}
                        onChange={(val) => setSelectedColumns(val)}
                      >
                        {columns.map((col) => (
                          <Checkbox
                            key={col.field}
                            value={col.field}
                            style={{ textTransform: "capitalize", display: "block", margin: "4px 0" }}
                          >
                            {col.headerName || col.field}
                          </Checkbox>
                        ))}
                      </CheckboxGroup>
                    </Dropdown.Menu>
                  </Popover>
                )}
              >
                <Tooltip title="Choose visible columns">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ViewColumn sx={{ fontSize: "0.9rem !important" }} />}
                    endIcon={<ArrowDownIcon style={{ fontSize: "0.7rem" }} />}
                    sx={toolbarBtnSx}
                  >
                    Columns
                  </Button>
                </Tooltip>
              </Whisper>
            )}

            {addMember && (
              <Tooltip title="Add Member">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add sx={{ fontSize: "0.9rem !important" }} />}
                  onClick={handleOpen}
                  sx={toolbarBtnSx}
                >
                  Add Member
                </Button>
              </Tooltip>
            )}

            {addDiscussion && (
              <Tooltip title="Add Discussion">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add sx={{ fontSize: "0.9rem !important" }} />}
                  onClick={handleOpen}
                  sx={toolbarBtnSx}
                >
                  Add Discussion
                </Button>
              </Tooltip>
            )}

            <Tooltip title="Save current column layout">
              <Button
                size="small"
                variant="text"
                startIcon={<Save sx={{ fontSize: "0.9rem !important" }} />}
                onClick={SaveSettings}
                sx={{ ...toolbarBtnSx, color: "success.main" }}
              >
                Save
              </Button>
            </Tooltip>

            <Tooltip title="Reset saved layout">
              <Button
                size="small"
                variant="text"
                startIcon={<RestoreFromTrash sx={{ fontSize: "0.9rem !important" }} />}
                onClick={deleteSettings}
                sx={{ ...toolbarBtnSx, color: "warning.main" }}
              >
                Reset
              </Button>
            </Tooltip>

            <Tooltip title="Clear all active filters">
              <Button
                size="small"
                variant="text"
                startIcon={<FilterAltOff sx={{ fontSize: "0.9rem !important" }} />}
                onClick={resetFilters}
                sx={{ ...toolbarBtnSx, color: "error.main" }}
              >
                Clear Filters
              </Button>
            </Tooltip>
          </Stack>

          {/* Centre slot — caller-provided component */}
          <Box sx={{ flex: 1, px: 1 }}>{headerComponent?.()}</Box>

          {/* Right toolbar group */}
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Chip
              label={`${rowCount} rows`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.75rem", height: 24 }}
            />
            <Tooltip title="Refresh data">
              <IconButton
                size="small"
                onClick={onGridReady}
                sx={{
                  background: alpha(muiTheme.palette.primary.main, 0.08),
                  "&:hover": { background: alpha(muiTheme.palette.primary.main, 0.18) },
                }}
              >
                {showLoading
                  ? <CircularProgress size={16} />
                  : <Refresh sx={{ fontSize: "1.1rem", color: "primary.main" }} />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      )}

      {/* AG Grid */}
      <Box sx={{ flex: 1, minHeight: 0 }} className={theme}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          autoSizeStrategy={autoSizeStrategy}
          getRowStyle={getRowStyle}
          rowSelection="single"
          pagination={true}
          onCellClicked={onRowSelected}
          onCellValueChanged={onCellValueChanged}
          onGridReady={getdata}
          onFilterChanged={updateFilteredRowNodes}
          onColumnMoved={(e) => setSettings(e.api.getColumnState())}
          overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading data…</span>'
          overlayNoRowsTemplate='<span class="ag-overlay-loading-center">No records found</span>'
        />
      </Box>
    </Box>
  );
}

// ─── PropTypes & Defaults ─────────────────────────────────────────────────────

AgGrid.propTypes = {
  columns: PropTypes.array,
  rowData: PropTypes.array,
  onGridReady: PropTypes.func,
  ExportFileName: PropTypes.string,
  GoTo: PropTypes.string,
  showHeaderItems: PropTypes.bool,
  passdata: PropTypes.bool,
  QuickSearch: PropTypes.bool,
  exportAllowed: PropTypes.bool,
  ChooseColumns: PropTypes.bool,
  showLoading: PropTypes.bool,
  addMember: PropTypes.bool,
  addDiscussion: PropTypes.bool,
  handleOpen: PropTypes.func,
  onRowSelectedData: PropTypes.func,
  handleViewModalOpen: PropTypes.func,
  onDataChange: PropTypes.func,
  headerComponent: PropTypes.func,
  setFilteredRows: PropTypes.func,
  Source: PropTypes.string,
  theme: PropTypes.string,
  setRowStyle: PropTypes.bool,
};

AgGrid.defaultProps = {
  onRowSelectedData: () => { },
  handleViewModalOpen: () => { },
  onDataChange: () => { },
  headerComponent: () => null,
  setFilteredRows: () => { },
};
